import React, { useCallback, useMemo, useState } from "react";
import CommissionDetailPlanCard from "./CommissionDetailPlanCard";
import CommissionDetailBonusCard from "./CommissionDetailBonusCard";
import CommissionDetailStudentDiscountCard from "./CommissionDetailStudentDiscountCard";
import CommissionPaymentSummaryCard from "./CommissionPaymentSummaryCard";

function CommissionDetailFinancialSection({
  planContext,
  params,
  student,
  provider,
  course,
  application,
  currency = "AUD",
  hasBonusEnabled = false,
  inquiryFeeRecord = null,
  commissionInvoices = [],
  inquiryFeesReadOnly = false,
  onInquiryFeesPaidSaved,
  onPlanCreated,
  onBonusSaved,
}) {
  const savedPlan = planContext?.existing || null;
  const activeBonus = Array.isArray(planContext?.bonuses)
    ? planContext.bonuses[0] || null
    : null;
  const installments = planContext?.installments || [];
  const summary = planContext?.summary || null;
  const displayCurrency = savedPlan?.currency || currency || "AUD";
  const [planDraft, setPlanDraft] = useState(null);
  const [discountDraft, setDiscountDraft] = useState(null);

  const effectivePlan = useMemo(() => {
    if (!savedPlan) return null;
    return {
      ...savedPlan,
      ...(planDraft || {}),
      ...(discountDraft || {}),
    };
  }, [savedPlan, planDraft, discountDraft]);

  const handlePlanDraftChange = useCallback((draft) => {
    setPlanDraft(draft);
  }, []);

  const handleDiscountDraftChange = useCallback((draft) => {
    setDiscountDraft(draft);
  }, []);

  const handlePlanCreated = useCallback(
    async (planId) => {
      setPlanDraft(null);
      if (typeof onPlanCreated === "function") {
        await onPlanCreated(planId);
      }
    },
    [onPlanCreated],
  );

  const handleBonusSaved = useCallback(async () => {
    setPlanDraft(null);
    setDiscountDraft(null);
    if (typeof onBonusSaved === "function") {
      await onBonusSaved();
    }
  }, [onBonusSaved]);

  return (
    <div className="space-y-3">
      <div
        className="cms-commission-financial-row flex w-full items-stretch gap-2"
      >
        <div className="min-w-0 flex-1">
          <CommissionDetailPlanCard
            planContext={planContext}
            params={params}
            student={student}
            provider={provider}
            course={course}
            application={application}
            currency={currency}
            onCreated={handlePlanCreated}
            onDraftChange={handlePlanDraftChange}
          />
        </div>
        <div className="min-w-0 flex-1">
          <CommissionDetailBonusCard
            planContext={planContext}
            params={params}
            student={student}
            provider={provider}
            course={course}
            application={application}
            currency={currency}
            onPlanCreated={handlePlanCreated}
            onSaved={handleBonusSaved}
          />
        </div>
        <div className="min-w-0 flex-1">
          <CommissionDetailStudentDiscountCard
            planContext={planContext}
            currency={currency}
            onSaved={handleBonusSaved}
            onDraftChange={handleDiscountDraftChange}
          />
        </div>
      </div>

      {savedPlan ? (
        <CommissionPaymentSummaryCard
          apiSummary={summary}
          installments={installments}
          plan={effectivePlan}
          bonus={activeBonus}
          currency={displayCurrency}
          inquiryFeeRecord={inquiryFeeRecord}
          commissionInvoices={commissionInvoices}
          inquiryFeesReadOnly={inquiryFeesReadOnly}
          inquiryFeeKeys={{
            applicationId: params?.appId || application?.id,
            clientId: params?.studentId || student?.id,
            providerId: params?.providerId || provider?.id,
            applicationCourseId:
              planContext?.defaults?.applicationCourseId ||
              course?.applicationCourseId,
          }}
          onInquiryFeesPaidSaved={onInquiryFeesPaidSaved}
        />
      ) : (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-8 text-center text-[13px] text-slate-500">
          Create a commission plan to see the payment summary for professional
          fee, commission, student discount, and bonus.
        </div>
      )}
    </div>
  );
}

export default CommissionDetailFinancialSection;
